/* ═══════════════════════════════════════════════════════════════════════════════
   Dashboard Filters Tour (v2.1.0)
   
   Feature update tour for the new centralized filter/slicer system.
   
   ARCHITECTURE:
   - Step 1: Non-interactive spotlight on filter button
   - Step 2 onEnter: Programmatically OPENS the filter modal by clicking trigger
   - Steps 2-3: `skipOverlay: true` means no tour overlay, just a floating tooltip.
     The filter modal's own backdrop provides dimming.
   - Step 4 onEnter: Programmatically CLOSES the filter modal
   - Steps 4-5: Normal spotlight overlay (no modal to conflict with)
   
   This avoids ALL z-index stacking context conflicts.
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

/**
 * Programmatically opens the filter modal by clicking the trigger button.
 * Includes a guard so it doesn't re-open if already open.
 */
function openFilterModal() {
  // If modal is already open, do nothing
  if (document.querySelector('.dfb-modal')) return;
  
  const trigger = document.querySelector('[data-tour-id="filter-trigger"]') as HTMLElement | null;
  trigger?.click();
}

/**
 * Programmatically closes the filter modal by clicking its close button.
 */
function closeFilterModal() {
  const closeBtn = document.querySelector('.dfb-modal-close') as HTMLElement | null;
  closeBtn?.click();
}

export const dashboardFiltersTour: TourDefinition = {
  id: 'dashboard_filters_v2_1',
  version: '2.1.0',
  type: 'feature_update',
  audience: 'all',
  page: 'dashboard',
  priority: 1,
  title: 'New Dashboard Update',
  subtitle: 'We\'ve added powerful new tools to help you analyze your data faster and smarter.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'What\'s New',
      description: 'Your dashboard just got a major upgrade. Here\'s a quick walkthrough of what changed.',
      icon: '✨',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Filter Button (non-interactive, just highlight) ──────
    {
      id: 'filter-trigger',
      type: 'spotlight',
      targetSelector: '[data-tour-id="filter-trigger"]',
      position: 'bottom',
      title: 'Centralized Filters',
      description: 'All your filters are now in one place. This button opens the new unified filter panel.',
      icon: '🎯',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 2: Filter Modal (skipOverlay, modal has own backdrop) ──
    {
      id: 'filter-modal',
      type: 'spotlight',
      targetSelector: '[data-tour-id="filter-modal"]',
      position: 'right',
      title: 'Filter Panel',
      description: 'Select Projects, Builders, Date Ranges, and Status, all in one unified panel. Filters cascade automatically.',
      icon: '⚡',
      actionLabel: 'Got it',
      spotlightPadding: 0,
      skipOverlay: true,
      onEnter: openFilterModal,
    },

    // ── Step 3: Date Presets (skipOverlay, still inside modal) ──────
    {
      id: 'date-presets',
      type: 'spotlight',
      targetSelector: '[data-tour-id="date-presets"]',
      position: 'right',
      title: 'Smart Date Ranges',
      description: 'Jump instantly to Last Month, This Quarter, Year-to-Date, or set a custom range. Every chart updates in real time.',
      icon: '📅',
      actionLabel: 'Next',
      skipOverlay: true,
      onEnter: openFilterModal, // Re-opens if user navigated back
    },

    // ── Step 4: Live Metrics (normal spotlight, modal closed) ───────
    {
      id: 'claims-summary',
      type: 'spotlight',
      targetSelector: '[data-tour-id="claims-summary"]',
      position: 'bottom',
      title: 'Live Metrics',
      description: 'Summary cards and trend charts now update in real time as you change filters. No more static numbers.',
      icon: '📊',
      actionLabel: 'Next',
      onEnter: closeFilterModal,
    },

    // ── Step 5: P&L Section ──────────────────────────────────────────
    {
      id: 'pnl-section',
      type: 'spotlight',
      targetSelector: '[data-tour-id="pnl-section"]',
      position: 'top',
      title: 'P&L Intelligence',
      description: 'The financial overview now responds to your date filters for deep time-based analysis.',
      icon: '💰',
      actionLabel: 'Next',
      onEnter: () => {
        // Custom scroll: leave ~220px room at top for the tooltip card
        const el = document.querySelector('[data-tour-id="pnl-section"]');
        if (el) {
          const rect = el.getBoundingClientRect();
          window.scrollTo({
            top: window.scrollY + rect.top - 220,
            behavior: 'smooth',
          });
        }
      },
    },

    // ── Step 6: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'You\'re All Set!',
      description: 'Start exploring your filtered dashboard. Click the filter button anytime to slice your data.',
      icon: '🎉',
      actionLabel: 'Start Exploring',
    },
  ],
};
